import { Component } from 'vue';
import { RouteRecordRaw } from 'vue-router';

import { RawRouteComponent } from '../../types';

/**
 * @param modules Map: key - file path, value - component (dynamic import / ES module / ...)
 * @param RouterViewComponent Component with `vue-router`s RouterView
 * @param modulesPrefixLength File path prefix length
 * @param modulesPostfixLength File path postfix length
 * @param dynamicPrefix Dynamic part prefix
 * @param dynamicPostfix Dynamic part postfix
 *
 * @returns Array
 */
export default (
  modules: Record<string, RawRouteComponent>,
  RouterViewComponent: Component,
  modulesPrefixLength = 0,
  modulesPostfixLength = 0,
  dynamicPrefix = '[',
  dynamicPostfix = ']'
) => {
  /*  */
  const partsDivider = '/';

  /*  */
  const routes: RouteRecordRaw[] = [];
  const routeRoot: RouteRecordRaw = {
    path: '',
    component: RouterViewComponent,
    children: routes,
  };

  /*  */
  for (const pathPure in modules) {
    /* Normalize path */
    const path = pathPure.slice(modulesPrefixLength, -modulesPostfixLength);

    /*  */
    const component = modules[pathPure];

    /*  */
    let needToAddOnly = false;
    let needToContinue = true;
    let pathIdx = 0;
    let pathIdxHelper = 0;
    let part: string;
    let route: RouteRecordRaw = routeRoot;
    let routeHelper: RouteRecordRaw | undefined;

    /*  */
    while (needToContinue) {
      pathIdx = path.indexOf(partsDivider, pathIdxHelper);

      /* Define part */
      if (pathIdx < 0) {
        part = path.slice(pathIdxHelper);
        needToContinue = false;
      } else {
        part = path.slice(pathIdxHelper, pathIdx);
      }

      /*  */
      if (part.startsWith(dynamicPrefix)) {
        pathIdxHelper = part.indexOf(dynamicPostfix);
        part = `:${part.slice(1, pathIdxHelper)}`;
      }

      /*  */
      if (needToAddOnly) {
        if (route.component !== RouterViewComponent) {
          /* Nest route & wrap it with RouterViewComponent */
          routeHelper = {
            path: part,
            component: route.component as RawRouteComponent,
          };

          route.children = [];
          route.component = RouterViewComponent;
        } else {
          routeHelper = {
            path: part,
            component,
          };
        }

        (route.children as RouteRecordRaw[]).push(routeHelper);
        route = routeHelper;
      } else {
        routeHelper = route.children?.find(i => i.path === part);

        if (routeHelper) {
          if (routeHelper.component !== RouterViewComponent) {
            routeHelper.children = [
              {
                path: '',
                component: routeHelper.component,
              },
            ] as RouteRecordRaw[];
            routeHelper.component = RouterViewComponent;
          } else if (!needToContinue) {
            (routeHelper.children as RouteRecordRaw[]).unshift({
              path: '',
              component,
            });
          }
        } else {
          routeHelper = {
            path: part,
            component,
          };

          if (route.children) {
            route.children.push(routeHelper);
          } else {
            route.children = [routeHelper];
          }

          needToAddOnly = true;
        }

        route = routeHelper;
      }

      ++pathIdx;
      pathIdxHelper = pathIdx;
    }
  }

  return routes;
};
